import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { useStatsigUser } from '@statsig/react-bindings';

import { GetAccountResponse } from 'apps-common/graphql/getAccount';
import { useGetAccount } from 'apps-common/hooks/useGetAccount';
import { useGetSellToCountries } from 'apps-common/hooks/useSellToCountries';
import { AccessUserTypes, MembershipType } from 'apps-common/types';
import { identify } from 'apps-common/utils/analytics';
import { throwError } from 'apps-common/utils/errorHandler';
import { logger } from 'apps-common/utils/logger';
import {
  isUserInactivePrepaidPending,
  membershipOfferingPrepaidMonths,
  subscriptionPrepaidMonths,
} from 'apps-common/utils/member';
import { Loader } from 'ui';

import { useRedirectPrepaidPendingUser } from '../hooks/useRedirectPrepaidPendingUser';
import { routes } from '../routes';
import { canUseExistingAddressForPaymentMethod } from '../utils/address';
import { getUserType } from '../utils/getUserType';
import { userTypeIsAllowed } from '../utils/userTypeIsAllowed';

// discriminated union types to always give either one of user list (allowedUsers | excludedUsers) but never give both or none
export type UserAccessProps =
  | { allowedUsers: AccessUserTypes[]; excludedUsers?: never }
  | { excludedUsers: AccessUserTypes[]; allowedUsers?: never };

export interface UserAccessCheckProps {
  userAccessRights: UserAccessProps;
  children: ReactNode;
}

const isAllowedUser = (
  membershipType: MembershipType | null | undefined,
  userAccessRights: UserAccessProps,
  currentSubscription: GetAccountResponse['account']['currentSubscription'] | undefined,
  paymentMethods: GetAccountResponse['account']['paymentMethods'] | undefined,
  isValidAddress: boolean,
  isValidCurrency: boolean,
) => {
  if (!membershipType) {
    return false;
  }

  const userType = getUserType(membershipType, currentSubscription, paymentMethods, isValidAddress, isValidCurrency);

  if ('excludedUsers' in userAccessRights) {
    return !userTypeIsAllowed(userAccessRights.excludedUsers!, userType);
  }

  return userTypeIsAllowed(userAccessRights.allowedUsers, userType);
};

const useRedirectToHomePageForNonAccessUsers = (
  accountFetched: boolean,
  countriesFetched: boolean,
  isAllowedUser: boolean,
) => {
  const navigate = useNavigate();
  useEffect(() => {
    if (accountFetched && countriesFetched && isAllowedUser == false) {
      navigate(routes.index);
    }
  }, [accountFetched, countriesFetched, isAllowedUser, navigate]);
};

const useUnfinishedOrUnverifiedUserCheck = () => {
  const { data: accountData } = useGetAccount();
  const membershipType = accountData?.account.membershipType;

  useEffect(() => {
    if (MembershipType.UNFINISHED === membershipType) {
      // The customer is not a legacy hardware user but they don't have subscription either (not even expired one).
      // They should be asked to finish the signup flow.
      throwError('errorOnB2CNoSubNoAccessOrNullMembershipType');
    } else if (MembershipType.UNVERIFIED === membershipType) {
      // The customer's account is unverified. They should be asked to verify it by requesting a new password
      // through forgot password feature.
      throwError('errorOnUnverifiedAccount');
    }
  }, [membershipType]);
};

export const UserAccessCheck = ({ userAccessRights, children }: UserAccessCheckProps) => {
  const { data: accountData, isFetching: isFetchingAccount, isError, error } = useGetAccount();
  const {
    data: countryData,
    isError: isErrorGetCountries,
    isFetching: isFetchingCountries,
    error: errorInGetCountries,
  } = useGetSellToCountries(!!accountData?.account);

  const { updateUserAsync } = useStatsigUser();

  const { currentSubscription, email, membershipType, paymentMethods, billToContact, shipToContact, isValidCurrency } =
    accountData?.account ?? {};
  const { membershipOffering } = accountData ?? {};

  const isInactivePrepaidPending = isUserInactivePrepaidPending(currentSubscription, accountData?.membershipOffering);
  const prepaidMonths = subscriptionPrepaidMonths(currentSubscription);
  const pendingPrepaidMonths = membershipOfferingPrepaidMonths(membershipOffering);
  const { pendingCancellation, subscriptionState } = currentSubscription ?? {};

  useEffect(() => {
    identify({
      traits: {
        email,
        prepaidMonths,
      },
    });

    updateUserAsync({ custom: { pendingCancellation, subscriptionState, pendingPrepaidMonths } });
  }, [subscriptionState, email, prepaidMonths, pendingPrepaidMonths, pendingCancellation, updateUserAsync]);

  useRedirectPrepaidPendingUser(isInactivePrepaidPending);

  const isValidAddress = canUseExistingAddressForPaymentMethod(
    shipToContact ?? null,
    billToContact ?? null,
    countryData ?? [],
  );

  const isUserAllowed = isAllowedUser(
    membershipType,
    userAccessRights,
    currentSubscription,
    paymentMethods,
    isValidAddress,
    isValidCurrency ?? true,
  );

  useUnfinishedOrUnverifiedUserCheck();
  useRedirectToHomePageForNonAccessUsers(!isFetchingAccount, !isFetchingCountries, isUserAllowed);

  if (isFetchingAccount || isFetchingCountries) {
    return <Loader />;
  }

  if (isError) {
    logger.error('Error getting account', error);
    return null;
  }

  if (isErrorGetCountries) {
    logger.error('Error getting countries', errorInGetCountries);
    return null;
  }

  if (!membershipType) {
    // Account not found. The customer should create the account and membership through the signup flow.
    logger.error('Membership type not found in account data.');
    return null;
  }

  if (!isUserAllowed) {
    // Return null to prevent flash of content before redirect
    return null;
  }
  return children;
};
